import { useState, useMemo, useEffect } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { PhaseTypeEnum, PhaseStructureTypeEnum } from '@vatos-pas/common';
import { MasterSheetContainer } from '../../containers/useMasterSheetContainer';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_REQUEST_RESOURCE,
  CAN_REQUEST_RESOURCE_ASSIGNED,
} from 'providers/permissions';
import { dateWithoutServerTimezone } from '../../utils/dateWithoutServerTimezone';
import {
  SKIPPED_PHASE_DISPLAY,
  getPhaseByType,
  isStartingApprovedPhase,
} from 'utils/phaseUtils';
import {
  getPhasesForJobType,
  getModalConfig,
  BUMP_PHASES,
  isTrimOptionPhase,
} from '../../constants/phaseConfigurations';
import { getPhaseTypeFromPriority } from '../../constants/priorityMappings';
import { useWorkflowConfig } from '../../config';
import { PhaseApprovalOptionValues } from '../../types/phaseOptions';

export const getResourceRequested = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const phaseJob = getPhaseByType(theJob.jobPhases, phaseType);
    if (!phaseJob) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(phaseJob, theJob)) return SKIPPED_PHASE_DISPLAY;
    if (phaseJob.dateRequested) {
      const dateRequested = dateWithoutServerTimezone(phaseJob.dateRequested);
      return `${
        dateRequested.getMonth() + 1
      }/${dateRequested.getDate()}/${dateRequested.getFullYear()}`;
    }
    return '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getResourceAllocated = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
  currentContractor = false,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;

    if (currentContractor && theJob?.job?.reqBumpoutTimesheet) {
      return jobPhase.contractorName || '';
    } else {
      return jobPhase.initialContractorName || '';
    }
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getSupervisorApproved = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;
    return jobPhase.approvingUserFullName || '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getRequestedBy = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;
    return jobPhase.requestingUserFullName || '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getStartDate = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase: any = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;

    if (
      (jobPhase.priority === 500 || jobPhase.priority === 400) &&
      jobPhase.approved &&
      jobPhase.customDateStart
    ) {
      const customDateStart = dateWithoutServerTimezone(
        jobPhase.customDateStart,
      );
      return `${
        customDateStart.getMonth() + 1
      }/${customDateStart.getDate()}/${customDateStart.getFullYear()}`;
    }

    if (jobPhase.dateStart) {
      const dateStart = dateWithoutServerTimezone(jobPhase.dateStart);
      return `${
        dateStart.getMonth() + 1
      }/${dateStart.getDate()}/${dateStart.getFullYear()}`;
    }

    return '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getApprovedDate = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;
    if (jobPhase.dateApproved) {
      const dateStart = dateWithoutServerTimezone(jobPhase.dateApproved);
      return `${
        dateStart.getMonth() + 1
      }/${dateStart.getDate()}/${dateStart.getFullYear()}`;
    }
    return '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const showCameraPhase = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (jobPhase) {
      return (
        !jobPhase.approved && jobPhase.contractorName && jobPhase.requested
      );
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const isApproved = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (jobPhase) {
      return jobPhase.approved;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const showPhaseImages = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    return (
      jobPhase &&
      (jobPhase.hasPhotos ||
        (jobPhase.jobPhasePhotos && jobPhase.jobPhasePhotos.length > 0))
    );
  } catch (error) {
    return false;
  }
};

interface UseTimelineLogicProps {
  geoCoords?: [number, number] | null;
  geoAccuracy?: number | null;
  geoLoading?: boolean;
  requestLocation?: () => void;
  geoLocationError?: string;
  showBump?: (bumpType: PhaseTypeEnum) => boolean;
  getNextStatusText?: (theJob: any) => string;
}

export const useTimelineLogic = ({
  geoCoords = null,
  geoAccuracy = null,
  geoLoading = false,
  requestLocation,
  geoLocationError,
  showBump,
  getNextStatusText: getNextStatusTextProp,
}: UseTimelineLogicProps) => {
  const { selectedJob, refreshJobPhases } = MasterSheetContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const config = useWorkflowConfig();
  const [garageEnabled] = useState(true);

  const [openContractorModal, setOpenContractorModal] = useState(false);
  const [selectedJobPhaseForEdit, setSelectedJobPhaseForEdit] =
    useState<any>(null);

  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedJobPhaseForHistory, setSelectedJobPhaseForHistory] =
    useState<any>(null);

  const [openRequestResource, setOpenRequestResource] = useState(false);
  const [selectedJobPhaseForModal, setSelectedJobPhaseForModal] =
    useState<any>(null);
  const [selectedPhaseType, setSelectedPhaseType] =
    useState<PhaseTypeEnum | null>(null);

  const [openPhotoUpload, setOpenPhotoUpload] = useState(false);
  const [selectedJobPhaseForPhotos, setSelectedJobPhaseForPhotos] =
    useState<any>(null);
  const [isViewPhotosMode, setIsViewPhotosMode] = useState(false);

  const [toggleGarage, setToggleGarage] = useState(false);

  const [openConfirmGoBack, setOpenConfirmGoBack] = useState(false);

  const [openInitialDate, setOpenInitialDate] = useState(false);
  const [openInitialFinishingDate, setOpenInitialFinishingDate] =
    useState(false);
  const [initialDate, setInitialDate] = useState('');
  const [initialFinishingDate, setInitialFinishingDate] = useState('');
  const [changedBySupervisor, setChangedBySupervisor] = useState(false);
  const [changedBySupervisorFinishing, setChangedBySupervisorFinishing] =
    useState(false);

  const phasesToRender = useMemo(() => {
    let phases;
    const jobPhaseStructureType = (selectedJob as any)?.job?.phaseStructureType;

    if (!jobPhaseStructureType) {
      phases = getPhasesForJobType(PhaseStructureTypeEnum.Drywall);
    } else {
      phases = getPhasesForJobType(jobPhaseStructureType);
    }

    // Helper function to check if a phase should be rendered for DrywallAndPaint
    // Trim phases only render when hasTrim is enabled; all others always show
    const shouldShowPhase = (phaseType: PhaseTypeEnum): boolean => {
      if (
        phaseType === PhaseTypeEnum.Trim1 ||
        phaseType === PhaseTypeEnum.Trim2
      ) {
        return !!(selectedJob as any)?.job?.hasTrim;
      }
      return true;
    };

    if (jobPhaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint) {
      return phases.filter((phaseType: PhaseTypeEnum) =>
        shouldShowPhase(phaseType),
      );
    }

    if (showBump) {
      return phases.filter((phaseType: PhaseTypeEnum) => {
        if (
          phaseType === PhaseTypeEnum.Bump1 ||
          phaseType === PhaseTypeEnum.Bump2 ||
          phaseType === PhaseTypeEnum.Bump3
        ) {
          return (selectedJob?.job?.currentJobPhasePriority || 0) >= 600;
        }
        if (
          phaseType === PhaseTypeEnum.Bump4 ||
          phaseType === PhaseTypeEnum.Bump5 ||
          phaseType === PhaseTypeEnum.DrywallTouchUp3 ||
          phaseType === PhaseTypeEnum.CMBluetape ||
          phaseType === PhaseTypeEnum.FinalBluetape
        ) {
          return showBump(phaseType);
        }
        return true;
      });
    }

    return phases;
  }, [
    (selectedJob as any)?.job?.phaseStructureType,
    (selectedJob as any)?.job?.hasTrim,
    selectedJob?.job?.currentJobPhasePriority,
    selectedJob?.jobPhase?.approved,
    selectedJob?.jobPhases,
    showBump,
  ]);

  useEffect(() => {
    if (selectedJob?.jobPhases) {
      const garageJobPhase = selectedJob.jobPhases.find(
        (jp: any) => jp.phaseType === PhaseTypeEnum.Garage,
      );
      if (garageJobPhase) {
        const currentPriority = selectedJob.job?.currentJobPhasePriority;
        // If currently on Garage phase (priority 200), force toggle ON
        // even if garageFirst was false at creation
        const isOnGaragePhase = currentPriority === 200;
        setToggleGarage(garageJobPhase.required || isOnGaragePhase);
      }
    }
  }, [selectedJob]);

  useEffect(() => {
    if (selectedJob?.jobPhases) {
      const sprayPhase: any = selectedJob.jobPhases.find(
        jobPhase => jobPhase.priority === 500,
      );
      const finishingPhase = selectedJob.jobPhases.find(
        jobPhase => jobPhase.priority === 400,
      );
      if (sprayPhase) {
        setInitialDate(sprayPhase.initialDateStart);
        setChangedBySupervisor(!!sprayPhase.customDateStart);
      }
      if (finishingPhase) {
        setInitialFinishingDate(finishingPhase.initialDateStart);
        setChangedBySupervisorFinishing(!!finishingPhase.customDateStart);
      }
    }
  }, [selectedJob]);

  const onChangeGarage = (
    theJob,
    toggleGarage,
    refreshJobPhases,
    setToggleGarage,
    _getPhase,
  ) => {
    const garagePhase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Garage);
    if (garagePhase) {
      const params = {
        id: garagePhase.id,
        data: {
          required: !toggleGarage,
        },
        previousData: garagePhase,
      };
      dataProvider.update('job-phase', params).then(_response => {
        refreshJobPhases();
        setToggleGarage(!toggleGarage);
      });
    }
  };

  const handleGoBack = () => {
    setOpenConfirmGoBack(true);
  };

  const goBackJob = async () => {
    if (!selectedJob?.jobPhase || !selectedJob?.jobPhases || !selectedJob?.job)
      return;

    const priority = selectedJob.job.currentJobPhasePriority || 100;

    const currentPhaseType = getPhaseTypeFromPriority(
      priority,
      (selectedJob as any)?.job?.phaseStructureType,
    );
    if (!currentPhaseType) return;

    const jobPhase = getPhaseByType(selectedJob.jobPhases, currentPhaseType);
    if (!jobPhase || !jobPhase.id) return;

    const params = {
      id: jobPhase.id,
      data: {} as any,
      previousData: jobPhase,
    };

    if (jobPhase.approved || (!jobPhase.contractorId && !jobPhase.requested)) {
      return;
    }

    if (jobPhase?.phaseType && BUMP_PHASES.includes(jobPhase.phaseType)) {
      params.data.contractorId = null;
      params.data.requested = false;
    } else {
      if (jobPhase.contractorId) {
        params.data.contractorId = null;
      } else if (jobPhase.requested) {
        params.data.requested = false;
      }
    }

    try {
      await dataProvider.update('job-phase', params);

      // Check if we need to reset options for this phase
      const isPaintPhaseWithOptions =
        jobPhase.phaseType === PhaseTypeEnum.FinalPaint ||
        jobPhase.phaseType === PhaseTypeEnum.InteriorPaint;

      // Trim Option is reverted by the backend on the phase PATCH above.
      if (isPaintPhaseWithOptions && selectedJob?.id) {
        const jobUpdateData: any = {};

        // Check if interiorOptions was set in this phase AND is currently true
        if (
          selectedJob.job.interiorOptionsFinished === true &&
          (selectedJob.job as any).interiorOptionsFinishedJobPhaseId ===
            jobPhase.id
        ) {
          jobUpdateData.interiorOptionsFinished = false;
          jobUpdateData.interiorOptionsFinishedJobPhaseId = null;
        }

        // Check if exteriorOptions was set in this phase AND is currently true
        if (
          selectedJob.job.exteriorOptionsFinished === true &&
          (selectedJob.job as any).exteriorOptionsFinishedJobPhaseId ===
            jobPhase.id
        ) {
          jobUpdateData.exteriorOptionsFinished = false;
          jobUpdateData.exteriorOptionsFinishedJobPhaseId = null;
        }

        if (Object.keys(jobUpdateData).length > 0) {
          await dataProvider.update('job', {
            id: selectedJob.id,
            data: jobUpdateData,
            previousData: selectedJob.job,
          });
        }
      }

      notify('Go back with success!');
      await refreshJobPhases();
      setOpenConfirmGoBack(false);
    } catch (error: any) {
      notify(`Go Back error: ${error.message}`, 'warning');
    }
  };

  const handleRequestResource = () => {
    if (selectedJob?.job && selectedJob?.jobPhases) {
      const priority = selectedJob.job.currentJobPhasePriority || 100;

      if (priority === 100) {
        return;
      }

      const currentPhaseType = getPhaseTypeFromPriority(
        priority,
        (selectedJob as any)?.job?.phaseStructureType,
      );

      if (currentPhaseType) {
        try {
          const currentPhase = getPhaseByType(
            selectedJob.jobPhases,
            currentPhaseType,
          );

          setSelectedJobPhaseForModal(currentPhase);
          setSelectedPhaseType(currentPhaseType);
          setOpenRequestResource(true);
        } catch (error) {
          console.error('Error getting current phase:', error);
        }
      }
    }
  };

  const handleLogGarage = () => {
    if (selectedJob?.jobPhases) {
      try {
        const garageJobPhase = selectedJob.jobPhases.find(
          (jp: any) => jp.phaseType === PhaseTypeEnum.Garage,
        );

        if (garageJobPhase) {
          setSelectedJobPhaseForModal(garageJobPhase);
          setSelectedPhaseType(PhaseTypeEnum.Garage);
          setOpenRequestResource(true);
        }
      } catch (error) {
        console.error('Error getting garage phase:', error);
      }
    }
  };

  const handleApprove = (phaseType: string) => {
    if (!selectedJob?.jobPhases) return;

    const jobPhase = getPhaseByType(
      selectedJob.jobPhases,
      phaseType as PhaseTypeEnum,
    );

    if (jobPhase) {
      setSelectedJobPhaseForPhotos(jobPhase);
      setIsViewPhotosMode(false);
      setOpenPhotoUpload(true);
    }
  };

  const handleEditContractor = (phaseType: PhaseTypeEnum) => {
    if (!selectedJob?.jobPhases) return;

    try {
      const jobPhase = getPhaseByType(selectedJob.jobPhases, phaseType);
      setSelectedJobPhaseForEdit(jobPhase);
      setOpenContractorModal(true);
    } catch (error) {
      console.error('Error getting job phase:', error);
    }
  };

  const handleViewPhotos = (phaseType: string) => {
    if (!selectedJob?.jobPhases) return;

    const jobPhase = getPhaseByType(
      selectedJob.jobPhases,
      phaseType as PhaseTypeEnum,
    );

    if (jobPhase) {
      setSelectedJobPhaseForPhotos(jobPhase);
      setIsViewPhotosMode(true);
      setOpenPhotoUpload(true);
    }
  };

  const handleHistoryModal = async (phaseType: PhaseTypeEnum) => {
    if (!selectedJob?.jobPhases) return;

    try {
      const jobPhase = getPhaseByType(selectedJob.jobPhases, phaseType);

      const contractorHistory = await dataProvider.getList<any>(
        'job-phase-contractor-history-report',
        {
          filter: { jobPhaseId: jobPhase.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
        },
      );

      const jobPhaseWithHistory = {
        ...jobPhase,
        jobPhaseContractorHistories: contractorHistory.data,
      };

      setSelectedJobPhaseForHistory(jobPhaseWithHistory);
      setOpenHistoryModal(true);
    } catch (error) {
      console.error('Error loading contractor history:', error);
      notify(`Error loading contractor history: ${error.message}`, 'error');
    }
  };

  const needStrike = (phaseType: string) => {
    if (!selectedJob?.jobPhases) return false;

    const jobPhaseFound = selectedJob.jobPhases.find(
      (jobPhase: any) => jobPhase.phaseType === phaseType,
    );

    if (!jobPhaseFound) return false;

    return parseInt(jobPhaseFound.contractorHistoryCount || '0') > 1;
  };

  const getNextStatusText = (theJob: any) => {
    const currentPhase = theJob?.jobPhase;
    if (!currentPhase) return 'No Phase';

    if (currentPhase?.phaseType === PhaseTypeEnum.Materials) {
      return 'Materials Sign Off';
    }

    // Check if this is a log phase
    const currentPhaseType = getPhaseTypeFromPriority(
      currentPhase?.priority,
      theJob?.job?.phaseStructureType,
    );
    const modalConfig = currentPhaseType
      ? getModalConfig(currentPhaseType)
      : null;
    const isLogPhase =
      currentPhaseType && modalConfig && modalConfig.modalType === 'log';

    if (currentPhase.approved === true) {
      return `${currentPhase?.phaseName}: Approved`;
    }

    if (currentPhase.requested === true) {
      if (currentPhase.contractorId) {
        return `${currentPhase?.phaseName}: Resources Allocated`;
      }
      return `${currentPhase?.phaseName}: Resources Requested`;
    } else {
      // For log phases, show "Log Contractor" — but bump phases in bumpout timesheet flow use "Request Resource"
      const isBumpPhase =
        currentPhaseType && BUMP_PHASES.includes(currentPhaseType);
      const isBumpoutActive = isBumpPhase && theJob?.job?.reqBumpoutTimesheet;
      const actionText =
        isLogPhase && !isBumpoutActive ? 'Log Contractor' : 'Request Resource';
      return `${currentPhase?.phaseName}: ${actionText}`;
    }
  };

  const checkRequestResourcePermission = () => {
    if (selectedJob?.jobPhase?.contractorId && !selectedJob.jobPhase.approved) {
      return hasPermission(CAN_REQUEST_RESOURCE_ASSIGNED);
    }
    return hasPermission(CAN_REQUEST_RESOURCE);
  };

  const isRequestResourceDisabled = () => {
    if (!selectedJob?.jobPhase) return true;

    return (
      selectedJob.jobPhase.priority < 200 ||
      selectedJob.jobPhase.approved === true ||
      selectedJob.jobPhase.requested === true
    );
  };

  const getRequestButtonLabel = () => {
    const currentPriority = selectedJob?.jobPhase?.priority;
    const currentPhaseType = currentPriority
      ? getPhaseTypeFromPriority(
          currentPriority,
          (selectedJob as any)?.job?.phaseStructureType,
        )
      : null;

    if (!currentPhaseType) return 'Request Resource';

    const labels = config.timeline.bumpRequestLabels;

    // If config has labels (Drywall), use them
    if (labels) {
      if (currentPhaseType === PhaseTypeEnum.Garage) {
        return labels.garage || 'Log Garage';
      }
      if (BUMP_PHASES.includes(currentPhaseType)) {
        return selectedJob?.job?.reqBumpoutTimesheet
          ? labels.bumpTimesheet || 'Request BumpOut'
          : labels.bumpDefault || 'Log BumpOut';
      }
      return 'Request Resource';
    }

    // D&P fallback: use modal config
    const modalConfig = getModalConfig(currentPhaseType);
    if (modalConfig.modalType === 'log') {
      return 'Log';
    }

    return 'Request Resource';
  };

  const goBackWarningMessage = useMemo(() => {
    const warnings = config.timeline.goBackWarnings;
    if (!warnings || Object.keys(warnings).length === 0) {
      return 'Are you sure that you want to go back?';
    }

    const currentPriority = selectedJob?.job?.currentJobPhasePriority;
    const currentPhaseType = currentPriority
      ? getPhaseTypeFromPriority(
          currentPriority,
          (selectedJob as any)?.job?.phaseStructureType,
        )
      : null;
    const isBumpPhase =
      currentPhaseType && BUMP_PHASES.includes(currentPhaseType);
    const isBumpoutActive = selectedJob?.job?.reqBumpoutTimesheet;

    if (isBumpPhase && isBumpoutActive && warnings.bump) {
      return warnings.bump;
    }

    return warnings.default || 'Are you sure that you want to go back?';
  }, [
    config.timeline.goBackWarnings,
    (selectedJob as any)?.job?.phaseStructureType,
    selectedJob?.job?.currentJobPhasePriority,
    selectedJob?.job?.reqBumpoutTimesheet,
  ]);

  const isGoBackDisabled = () => {
    if (!selectedJob?.jobPhase) return true;

    const isJobPhaseApproved = selectedJob.jobPhase.approved;
    const hasContractor = selectedJob.jobPhase.contractorId;
    const isRequested = selectedJob.jobPhase.requested;

    return (
      isJobPhaseApproved ||
      (!hasContractor && !isRequested) ||
      selectedJob.jobPhase.priority < 200
    );
  };

  const isGarageDisabled = () => {
    if (!selectedJob?.jobPhase) return true;

    const jobPhase = selectedJob.jobPhase;
    const garagePhase = getPhaseByType(
      selectedJob.jobPhases,
      PhaseTypeEnum.Garage,
    );

    if (
      jobPhase.priority < 200 ||
      jobPhase.priority > 300 ||
      jobPhase.requested === true ||
      (garagePhase && garagePhase.dateApproved)
    ) {
      return true;
    }

    return false;
  };

  const approveJob = async (options: PhaseApprovalOptionValues) => {
    const jobPhase = selectedJobPhaseForPhotos;
    if (jobPhase && selectedJob?.job) {
      const params: any = {
        id: jobPhase.id,
        data: { approved: true },
        previousData: jobPhase,
      };
      const isTrimOptionApproval = Boolean(
        selectedJob.job.hasTrimOptions && isTrimOptionPhase(jobPhase.phaseType),
      );

      if (isTrimOptionApproval) {
        // The backend finalizes the job's Trim Option while approving the phase.
        params.data.trimOptionSelected = options.trimOptionSelected;
      }

      if (geoCoords) {
        params.data.approvedGeo = {
          type: 'Point',
          coordinates: geoCoords,
        };
        params.data.approvedGeoAccuracy = geoAccuracy;
      }

      try {
        await dataProvider.update('job-phase', params);

        const isPaintPhaseWithOptions =
          jobPhase.phaseType === PhaseTypeEnum.FinalPaint ||
          jobPhase.phaseType === PhaseTypeEnum.InteriorPaint;

        const updateData: any = {};

        if (isPaintPhaseWithOptions) {
          if (
            selectedJob.job.interiorOptionsFinished !==
            options.interiorOptionsCompleted
          ) {
            updateData.interiorOptionsFinished =
              options.interiorOptionsCompleted;
            updateData.interiorOptionsFinishedJobPhaseId =
              options.interiorOptionsCompleted ? jobPhase.id : null;
          }

          if (
            selectedJob.job.exteriorOptionsFinished !==
            options.exteriorOptionsCompleted
          ) {
            updateData.exteriorOptionsFinished =
              options.exteriorOptionsCompleted;
            updateData.exteriorOptionsFinishedJobPhaseId =
              options.exteriorOptionsCompleted ? jobPhase.id : null;
          }
        }

        if (Object.keys(updateData).length > 0 && selectedJob.id) {
          await dataProvider.update('job', {
            id: selectedJob.id,
            data: updateData,
            previousData: selectedJob as any,
          });
        }

        await refreshJobPhases();
        notify(
          isTrimOptionApproval
            ? 'Phase approved successfully.'
            : 'Job Approved!',
        );
        setOpenPhotoUpload(false);
      } catch (error: any) {
        if (isTrimOptionApproval) {
          notify('Unable to Approve Phase. Please try again later.', 'warning');
        } else {
          notify(`Approve Job Error: ${error.message}`, 'warning');
        }
      }
    }
  };

  return {
    selectedJob,
    refreshJobPhases,
    hasPermission,
    garageEnabled,
    openContractorModal,
    setOpenContractorModal,
    selectedJobPhaseForEdit,
    openHistoryModal,
    setOpenHistoryModal,
    selectedJobPhaseForHistory,
    openRequestResource,
    setOpenRequestResource,
    selectedJobPhaseForModal,
    setSelectedJobPhaseForModal,
    selectedPhaseType,
    setSelectedPhaseType,
    openPhotoUpload,
    setOpenPhotoUpload,
    selectedJobPhaseForPhotos,
    isViewPhotosMode,
    toggleGarage,
    setToggleGarage,
    openConfirmGoBack,
    setOpenConfirmGoBack,
    openInitialDate,
    setOpenInitialDate,
    openInitialFinishingDate,
    setOpenInitialFinishingDate,
    initialDate,
    initialFinishingDate,
    changedBySupervisor,
    changedBySupervisorFinishing,
    getPhase: getPhaseByType,
    phasesToRender,
    onChangeGarage,
    handleGoBack,
    goBackJob,
    handleRequestResource,
    handleLogGarage,
    handleApprove,
    handleEditContractor,
    handleViewPhotos,
    handleHistoryModal,
    needStrike,
    getNextStatusText,
    checkRequestResourcePermission,
    isRequestResourceDisabled,
    getRequestButtonLabel,
    isGoBackDisabled,
    isGarageDisabled,
    approveJob,
    geoCoords,
    geoAccuracy,
    geoLoading,
    requestLocation,
    geoLocationError,
    getNextStatusTextProp,
    goBackWarningMessage,
  };
};
